import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { useSubmitSectorSupportMutation } from "@/state/submissionsApi";

type Props = {
  sectorName: string;
  sectorIcon: string;
  onClose: () => void;
}

const supportOptions = [
  "supportTypeRegistration",
  "supportTypeIdentification",
  "supportTypeBidWriting",
  "supportTypeCompliance",
  "supportTypeFinancial",
  "supportTypeBidBond",
  "supportTypePerformanceBond",
  "supportTypeFull",
  "supportTypeOther",
];

const SectorSupportSchema = z.object({
  companyName: z.string().min(1, "Company name is required"),
  contactName: z.string().min(1, "Contact name is required"),
  email: z.email("Invalid email address"),
  phone: z.string().optional(),
  country: z.string().optional(),
  supportType: z.string().optional(),
  additionalInfo: z.string().max(500).optional(),
});

type SectorSupportFormValues = z.infer<typeof SectorSupportSchema>;

export default function SectorSupportModal({ sectorName, sectorIcon, onClose }: Props) {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSectorSupport, { isLoading }] = useSubmitSectorSupportMutation();

  const { register, handleSubmit, formState: { errors }, watch, reset } = useForm<SectorSupportFormValues>({
    resolver: zodResolver(SectorSupportSchema),
    defaultValues: {
      companyName: "", contactName: "", email: "",
      phone: "", country: "", supportType: "", additionalInfo: "",
    },
  });

  const additionalInfoLength = watch("additionalInfo")?.length ?? 0;

  const onSubmit = async (data: SectorSupportFormValues) => {
    setSubmitError("");
    try {
      await submitSectorSupport({
        sector: sectorName,
        companyName: data.companyName,
        contactName: data.contactName,
        email: data.email,
        phone: data.phone || undefined,
        country: data.country || undefined,
        supportType: data.supportType || undefined,
        additionalInfo: data.additionalInfo || undefined,
      }).unwrap();
      setSubmitted(true);
      reset();
    } catch (err) {
      const message = (err as any)?.data?.error?.message;
      setSubmitError(message ?? "Something went wrong. Please try again.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-xl shadow-2xl relative overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-[#0A1628] px-8 py-6 flex items-center gap-4 shrink-0">
          <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-white/10">
            <i className={`${sectorIcon} text-xl text-[#00C2C2]`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[#00C2C2] text-xs font-semibold tracking-widest uppercase mb-0.5">
              {t("sectorFormSectorLabel")}
            </p>
            <h3 className="text-white font-extrabold text-lg leading-tight truncate">
              {sectorName}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-white/60 hover:bg-white/20 hover:text-white transition-colors cursor-pointer shrink-0"
            aria-label="Close"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-8 py-6">
          {submitted ? (
            <div className="text-center py-10">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#0A1628] text-white text-2xl mx-auto mb-4">
                <i className="ri-check-line" />
              </div>
              <h4 className="text-lg font-bold text-[#0A1628] mb-2">
                {t("sectorFormSuccessTitle")}
              </h4>
              <p className="text-gray-500 text-sm">{t("sectorFormSuccessBody")}</p>
              <button
                onClick={() => setSubmitted(false)}
                className="mt-6 text-[#00C2C2] text-sm font-semibold hover:underline cursor-pointer whitespace-nowrap"
              >
                {t("submitAnother")}
              </button>
            </div>
          ) : (
            <>
              <p className="text-gray-500 text-sm mb-6 leading-relaxed">
                {t("sectorFormSubtitle")}
              </p>

              <form id="sector-support-form" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
                {submitError && (
                  <p className="text-xs text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{submitError}</p>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide">
                      {t("supportFormCompany")} *
                    </label>
                    <input type="text" placeholder={t("company")} {...register("companyName")} className="text-sm border border-gray-200 rounded-lg px-4 py-3 bg-[#f8fafc] focus:outline-none focus:border-[#00C2C2] text-[#0A1628] placeholder-gray-400 transition-colors" />
                    {errors.companyName && <p className="text-xs text-red-500">{errors.companyName.message}</p>}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide">
                      {t("supportFormContact")} *
                    </label>
                    <input type="text" placeholder={t("fullName")} {...register("contactName")} className="text-sm border border-gray-200 rounded-lg px-4 py-3 bg-[#f8fafc] focus:outline-none focus:border-[#00C2C2] text-[#0A1628] placeholder-gray-400 transition-colors" />
                    {errors.contactName && <p className="text-xs text-red-500">{errors.contactName.message}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide">
                      {t("supportFormEmail")} *
                    </label>
                    <input type="email" placeholder="email@company.com" {...register("email")} className="text-sm border border-gray-200 rounded-lg px-4 py-3 bg-[#f8fafc] focus:outline-none focus:border-[#00C2C2] text-[#0A1628] placeholder-gray-400 transition-colors" />
                    {errors.email && <p className="text-xs text-red-500">{errors.email.message}</p>}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide">
                      {t("supportFormPhone")}
                    </label>
                    <input type="tel" placeholder="+250 7XX XXX XXX" {...register("phone")} className="text-sm border border-gray-200 rounded-lg px-4 py-3 bg-[#f8fafc] focus:outline-none focus:border-[#00C2C2] text-[#0A1628] placeholder-gray-400 transition-colors" />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide">
                    {t("supportFormCountry")}
                  </label>
                  <select {...register("country")} className="text-sm border border-gray-200 rounded-lg px-4 py-3 bg-[#f8fafc] focus:outline-none focus:border-[#00C2C2] text-[#0A1628] transition-colors cursor-pointer">
                    <option value="">{t("supportFormSelectCountry")}</option>
                    <option value="Rwanda">Rwanda</option>
                    <option value="Kenya">Kenya</option>
                    <option value="Tanzania">Tanzania</option>
                    <option value="Uganda">Uganda</option>
                    <option value="Ethiopia">Ethiopia</option>
                    <option value="Burundi">Burundi</option>
                    <option value="DRC">DRC</option>
                    <option value="Other">{t("supportTypeOther")}</option>
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide">
                    {t("supportFormType")}
                  </label>
                  <select {...register("supportType")} className="text-sm border border-gray-200 rounded-lg px-4 py-3 bg-[#f8fafc] focus:outline-none focus:border-[#00C2C2] text-[#0A1628] transition-colors cursor-pointer">
                    <option value="">{t("supportFormSelectSupport")}</option>
                    {supportOptions.map((opt) => (
                      <option key={opt} value={t(opt)}>{t(opt)}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide">
                    {t("supportFormInfo")}
                  </label>
                  <textarea rows={3} maxLength={500} placeholder="Any additional context about your requirement..." {...register("additionalInfo")} className="text-sm border border-gray-200 rounded-lg px-4 py-3 bg-[#f8fafc] focus:outline-none focus:border-[#00C2C2] text-[#0A1628] placeholder-gray-400 resize-none transition-colors" />
                  <p className="text-xs text-gray-400 text-right">{additionalInfoLength}/500</p>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-[#0A1628] text-white text-sm font-bold py-3 rounded-lg hover:bg-[#0A1628]/90 transition-colors whitespace-nowrap disabled:opacity-60 cursor-pointer flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      {t("processing")}
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line" />
                      {t("sectorFormSubmit")}
                    </>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
